import { useState, useEffect } from 'react';
import { ordersService } from '@/shared/services/ordersService';
import type { Order } from '@/shared/types/orders';
import { OrderDetailModal } from '@/features/orders/components/OrderDetailModal';
import styles from './IncidentsPage.module.css';

export function IncidentsPage() {
    const [activeTab, setActiveTab] = useState<'OPEN' | 'REFUND_PENDING' | 'RESOLVED'>('OPEN');
    const [incidents, setIncidents] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    useEffect(() => {
        loadIncidents();
    }, [activeTab]);

    const loadIncidents = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await ordersService.getIncidents(activeTab);
            setIncidents(data.orders);
        } catch (err) {
            console.error('Error loading incidents:', err);
            setError('Error al cargar las incidencias. Intenta recargar.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp?: any) => {
        if (!timestamp) return 'N/A';

        let date: Date;

        if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds !== undefined) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else {
            return 'N/A';
        }

        if (isNaN(date.getTime())) return 'Fecha inválida';

        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getElapsedMinutes = (timestamp: { seconds: number; nanoseconds: number }) => {
        const created = new Date(timestamp.seconds * 1000);
        const now = new Date();
        const diff = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));
        return diff < 0 ? 0 : diff; // Asegurar que no sea negativo
    };

    if (loading && incidents.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Cargando incidencias...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>🚨 Incidencias de Pedidos</h1>
                    <p className={styles.subtitle}>
                        {activeTab === 'OPEN'
                            ? 'Pedidos que requieren atención inmediata'
                            : activeTab === 'REFUND_PENDING'
                                ? 'Pedidos cancelados con pagos digitales que requieren reembolso'
                                : 'Historial de incidencias resueltas'}
                    </p>
                </div>
                <button onClick={loadIncidents} className={styles.refreshButton} disabled={loading}>
                    {loading ? '⌛ Cargando...' : '🔄 Actualizar'}
                </button>
            </div>

            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'OPEN' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('OPEN')}
                >
                    ⚠️ Activas ({activeTab === 'OPEN' ? incidents.length : '...'})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'REFUND_PENDING' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('REFUND_PENDING')}
                >
                    💰 Por Reembolsar ({activeTab === 'REFUND_PENDING' ? incidents.length : '...'})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'RESOLVED' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('RESOLVED')}
                >
                    ✅ Historial
                </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {incidents.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        {activeTab === 'OPEN' ? '✅' : activeTab === 'REFUND_PENDING' ? '💵' : '📁'}
                    </div>
                    <h2>
                        {activeTab === 'OPEN'
                            ? '¡Todo está en orden!'
                            : activeTab === 'REFUND_PENDING'
                                ? '¡Sin reembolsos pendientes!'
                                : 'No hay historial'}
                    </h2>
                    <p>
                        {activeTab === 'OPEN'
                            ? 'No hay incidencias activas en este momento.'
                            : activeTab === 'REFUND_PENDING'
                                ? 'No hay pedidos cancelados esperando devolución de dinero.'
                                : 'Aún no se han resuelto incidencias.'}
                    </p>
                </div>
            ) : (
                <div className={styles.incidentsList}>
                    {incidents.map((incident) => {
                        const elapsedMinutes = incident.incident
                            ? getElapsedMinutes(incident.incident.createdAt)
                            : 0;

                        const isRefundPending = activeTab === 'REFUND_PENDING';

                        return (
                            <div key={incident.orderId} className={`
                                ${styles.incidentCard} 
                                ${activeTab === 'RESOLVED' ? styles.resolvedCard : ''}
                                ${isRefundPending ? styles.refundCard : ''}
                            `}>
                                <div className={styles.incidentHeader}>
                                    <div className={styles.incidentBadge}>
                                        <span className={styles.incidentIcon}>
                                            {activeTab === 'OPEN' ? '⚠️' : isRefundPending ? '💰' : '✅'}
                                        </span>
                                        <span className={styles.incidentCode}>{incident.orderCode}</span>
                                    </div>
                                    <span className={styles.incidentTime}>
                                        {activeTab === 'RESOLVED'
                                            ? `Resuelto: ${formatDate(incident.incident?.resolvedAt)}`
                                            : isRefundPending
                                                ? 'REEMBOLSO REQUERIDO'
                                                : `Hace ${elapsedMinutes} min`}
                                    </span>
                                </div>

                                <div className={styles.incidentBody}>
                                    <div className={styles.incidentInfo}>
                                        <h3 className={styles.incidentTitle}>
                                            {activeTab === 'RESOLVED'
                                                ? (incident.incident?.resolutionNotes || 'Incidencia Cerrada')
                                                : (incident.incident?.reasonText || 'Sin descripción')
                                            }
                                        </h3>
                                        <div className={styles.incidentMeta}>
                                            <span>📅 {formatDate(incident.createdAt)}</span>
                                            <span>🏪 {incident.pickup.restaurantName}</span>
                                            <span>📍 Estado: <strong className={styles[`status${incident.status.replace(/\s+/g, '')}`]}>{incident.status}</strong></span>
                                        </div>

                                        {activeTab === 'RESOLVED' && (
                                            <div className={styles.resolutionNotes} style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                                <strong>⚠️ Motivo Original:</strong> {incident.incident?.reasonText || 'N/A'}
                                                <div className={styles.resolvedBy}>
                                                    Resuelto por: {incident.incident?.resolvedBy || 'Sistema'}
                                                </div>
                                            </div>
                                        )}

                                        {isRefundPending && (
                                            <div className={styles.resolutionNotes} style={{ backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>
                                                <strong>ℹ️ Nota de Cancelación:</strong> {incident.incident?.resolutionNotes || 'El sistema canceló el pedido por tiempo agotado.'}
                                                <div style={{ marginTop: '8px', fontWeight: 'bold' }}>
                                                    Debe procesar el reembolso en el panel de pagos antes de cerrar esta incidencia.
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.incidentDetails}>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Tipo:</span>
                                            <span className={styles.detailValue}>
                                                {incident.type === 'delivery' ? '🛵 Delivery' : '🏪 Pickup'}
                                            </span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Total:</span>
                                            <span className={styles.detailValue}>
                                                Bs. {incident.financial.total.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Pago:</span>
                                            <span className={styles.detailValue}>
                                                {incident.payment.method === 'CASH_ON_DELIVERY' ? '💵 Efectivo' : '💳 Digital'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.incidentActions}>
                                    <button
                                        className={styles.actionViewOrder}
                                        onClick={() => setSelectedOrderId(incident.orderId)}
                                    >
                                        Ver Pedido Completo
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedOrderId && (
                <OrderDetailModal
                    orderId={selectedOrderId}
                    onClose={() => {
                        setSelectedOrderId(null);
                        loadIncidents();
                    }}
                />
            )}
        </div>
    );
}
